import {
  EMOJIS_SUCCESS,
  LABEL_INCORRECT,
  LABEL_LEARNING,
  LABEL_MASTERED,
  LABEL_NEW,
  MASTERY_THRESHOLD_MASTERED,
  PRACTICE_DECK_SIZE,
  TEST_PASS_THRESHOLD,
} from "./constants.js";
import { triggerGlitter } from "./glitter.js";
import { els, state } from "./state.js";
import { getQuestionHistory, saveStats } from "./storage.js";
import { calculateMasteryLevel, getMasteryInfo, preparePracticeDeck, shuffleArray } from "./utils.js";

export const startPracticeMode = (specificQId = null, topic = null, reviewOnly = false) => {
  state.mode = "practice";
  state.practice.topic = topic;
  state.practice.sessionCount = 0;
  state.practice.reviewOnly = reviewOnly;
  els.quiz.style.display = "block";
  els.timer.style.display = "none";
  els.topicLabel.textContent = topic || (reviewOnly ? "Only Needs Review" : "");

  if (specificQId) {
    const q = state.allQuestions.find(item => item.id === specificQId);
    if (q) {
      state.currentQuestion = q;
      const sourceQuestions = topic
        ? state.allQuestions.filter(item => item.section === topic)
        : state.allQuestions;
      // Generate deck but ensure the specific question is NOT in the follow-up deck
      state.practice.deck = preparePracticeDeck(
        sourceQuestions,
        state.userProgress.history,
        PRACTICE_DECK_SIZE,
        reviewOnly,
      )
        .filter(item => item.id !== specificQId);
      renderQuestion();
      return;
    }
  }

  const sourceQuestions = topic
    ? state.allQuestions.filter(q => q.section === topic)
    : state.allQuestions;

  state.practice.deck = preparePracticeDeck(
    sourceQuestions,
    state.userProgress.history,
    PRACTICE_DECK_SIZE,
    reviewOnly,
  );

  loadNextQuestion(false);
};

export const startFlashcardMode = (specificQId = null, reviewOnly = false) => {
  state.mode = "flashcard";
  state.practice.sessionCount = 0;
  state.practice.reviewOnly = reviewOnly;
  els.quiz.style.display = "block";
  els.timer.style.display = "none";

  if (specificQId) {
    const q = state.allQuestions.find((item) => item.id === specificQId);
    if (q) {
      state.currentQuestion = q;
      state.practice.deck = []; // Fix: Don't put q in deck, it's already current
      renderQuestion();
      return;
    }
  }

  // Use the same smart deck logic as practice mode
  state.practice.deck = preparePracticeDeck(
    state.allQuestions,
    state.userProgress.history,
    PRACTICE_DECK_SIZE,
    reviewOnly,
  );

  loadNextQuestion(false);
};

export const startTestMode = (count, timeInMinutes) => {
  state.mode = "test";
  state.test.score = 0;
  state.test.totalQuestions = count;
  state.test.timeLeft = timeInMinutes * 60;
  state.test.currentIndex = 0;

  state.test.questions = shuffleArray([...state.allQuestions]).slice(0, count);

  els.quiz.style.display = "block";
  els.timer.style.display = "block";
  updateTimerUI();

  state.test.timerInterval = setInterval(() => {
    state.test.timeLeft--;
    updateTimerUI();
    if (state.test.timeLeft <= 0) {
      finishTest();
    }
  }, 1000);

  loadNextQuestion(false);
};

export const loadNextQuestion = (incrementIndex = true) => {
  if (state.mode === "practice" || state.mode === "flashcard") {
    if (incrementIndex && state.mode === "flashcard") {
      state.practice.sessionCount++;
    }
    if (state.practice.deck.length === 0) {
      const sourceQuestions = state.practice.topic
        ? state.allQuestions.filter(q => q.section === state.practice.topic)
        : state.allQuestions;
      state.practice.deck = preparePracticeDeck(
        sourceQuestions,
        state.userProgress.history,
        PRACTICE_DECK_SIZE,
        state.practice.reviewOnly,
      );
    }
    state.currentQuestion = state.practice.deck.shift();
  } else {
    if (incrementIndex) state.test.currentIndex++;
    if (state.test.currentIndex >= state.test.totalQuestions) {
      finishTest();
      return;
    }
    state.currentQuestion = state.test.questions[state.test.currentIndex];
  }
  renderQuestion();
};

export const renderQuestion = () => {
  window.scrollTo(0, 0);
  const q = state.currentQuestion;
  els.question.textContent = q.question;
  els.options.innerHTML = "";
  const nextBtnContainer = document.getElementById("next-btn-container");
  if (nextBtnContainer) nextBtnContainer.style.display = "none";

  els.nextBtn.style.display = "none";
  els.explanationText.style.display = "none";
  els.explanationText.textContent = "";

  // Reset Flashcard State
  if (state.mode === "flashcard") {
    els.question.style.display = "none";
    els.questionImageContainer.style.display = "none";
    els.options.style.display = "none";
    els.flashcardContainer.style.display = "block";

    // Prevent animation when resetting state (avoid peeking at next answer)
    // New Logic: Update Front immediately, flip back, wait for transition, then update Back.

    // 1. Update Front (Question)
    els.flashcardFront.innerHTML = "";
    const qText = document.createElement("div");
    qText.className = "question-text";
    qText.textContent = q.question;
    els.flashcardFront.appendChild(qText);

    if (q.image) {
      const img = document.createElement("img");
      img.src = q.image;
      els.flashcardFront.appendChild(img);
    }

    // 2. Trigger Flip Back
    const wasFlipped = els.flashcardContainer.classList.contains("flipped");
    els.flashcardContainer.classList.remove("flipped");

    // 3. Update Back (Answer) after transition (600ms)
    // We only delay if it was previously flipped (i.e. we are advancing questions)
    // If it wasn't flipped (initial load), we can update immediately.
    const updateBack = () => {
      const correctIdx = q.answers.findIndex((a) => a.correct);
      const correctOpt = q.answers[correctIdx];
      const label = ["a", "b", "c", "d"][correctIdx] || "";
      const answerText = correctOpt ? (correctOpt.text || "").trim() : "Error: No correct answer found.";

      // Only show label (a, b, c, d) if there's an image or if the answer text is empty
      const showLabel = q.image || !answerText;
      const displayText = showLabel ? `${label}) ${answerText}`.trim() : answerText;

      els.flashcardBack.innerHTML = `<div class="flashcard-back-content">${displayText}</div>`;
    };

    // Simply using setTimeout to match CSS transition.
    // However, on first load, we don't want to wait.
    // We can check if content is empty or use a flag, but simpler:
    // If the back content is empty, update immediately.
    if (els.flashcardBack.innerHTML === "" || !wasFlipped) {
      updateBack();
    } else {
      setTimeout(updateBack, 600);
    }

    // Click to flip
    els.flashcardContainer.onclick = () => {
      els.flashcardContainer.classList.toggle("flipped");
      const nextBtnContainer = document.getElementById("next-btn-container");
      if (nextBtnContainer) nextBtnContainer.style.display = "block";
      els.nextBtn.style.display = "block";
    };
  } else {
    // Normal Quiz Mode
    els.question.style.display = "block";
    els.options.style.display = "grid";
    els.flashcardContainer.style.display = "none";
    els.flashcardContainer.onclick = null;

    if (q.image) {
      els.questionImage.src = q.image;
      els.questionImageContainer.style.display = "block";
    } else {
      els.questionImageContainer.style.display = "none";
    }
  }

  const history = getQuestionHistory(q.id);
  const mastery = calculateMasteryLevel(history);
  els.masteryBadgeContainer.style.display = "flex";
  els.masteryBadgeContainer.innerHTML = "";

  const info = getMasteryInfo(mastery);
  const badge = createBadgeElement(info.label, info.colorClass, mastery);
  els.masteryBadgeContainer.appendChild(badge);

  if (state.mode === "flashcard") {
    updateStatsUI();
    return;
  }

  const allEmpty = q.answers.every(a => !a.text || a.text.trim() === "");
  els.options.classList.toggle("grid-2x2", allEmpty);

  const shuffledOptions = allEmpty ? [...q.answers] : shuffleArray([...q.answers]);
  const labels = ["a", "b", "c", "d"];

  shuffledOptions.forEach((opt, index) => {
    const btn = document.createElement("button");
    btn.className = "option-btn";
    if (opt.correct) {
      btn.classList.add("is-correct");
    }
    btn.innerHTML = `<span class="option-label">${labels[index]})</span> <span class="option-text">${
      opt.text || ""
    }</span>`;
    btn.onclick = () => handleAnswer(btn, opt.correct, shuffledOptions);
    els.options.appendChild(btn);
  });

  updateStatsUI();
};

export const handleAnswer = (selectedBtn, isCorrect, allOptions) => {
  const buttons = els.options.querySelectorAll("button");
  buttons.forEach(b => (b.disabled = true));

  if (isCorrect) {
    selectedBtn.classList.add("correct");
  } else {
    selectedBtn.classList.add("incorrect");

    buttons.forEach(b => {
      if (b.classList.contains("is-correct")) {
        b.classList.add("correct");
      }
    });
  }

  const qId = state.currentQuestion.id;
  const oldLevel = calculateMasteryLevel(state.userProgress.history[qId]);

  if (!state.userProgress.history[qId]) {
    state.userProgress.history[qId] = [];
  }

  state.userProgress.history[qId].push({
    date: Date.now(),
    correct: isCorrect,
  });

  if (state.mode === "practice") {
    state.practice.sessionCount++;
    saveStats();
  } else if (state.mode === "test") {
    if (isCorrect) state.test.score++;
  }

  if (isCorrect) {
    state.sessionStreak++;
    if (state.mode === "practice" && state.sessionStreak >= 3) {
      const intensity = Math.min(100, 10 + (state.sessionStreak - 3) * 20);
      triggerGlitter({ emojis: EMOJIS_SUCCESS, count: intensity });
    }
  } else {
    state.sessionStreak = 0;
  }

  const newHistory = state.userProgress.history[qId];
  const newLevel = calculateMasteryLevel(newHistory);

  if (newLevel !== oldLevel) {
    const oldInfo = getMasteryInfo(oldLevel);
    const newInfo = getMasteryInfo(newLevel);

    if (oldInfo.label !== newInfo.label) {
      els.masteryBadgeContainer.innerHTML = "";
      els.masteryBadgeContainer.appendChild(createBadgeElement(oldInfo.label, oldInfo.colorClass, oldLevel));

      const arrow = document.createElement("span");
      arrow.className = "mastery-badge-arrow";
      arrow.textContent = "→";
      els.masteryBadgeContainer.appendChild(arrow);

      const newBadge = createBadgeElement(newInfo.label, newInfo.colorClass, newLevel);
      newBadge.classList.add("animate-glow");
      els.masteryBadgeContainer.appendChild(newBadge);
    } else {
      // Just update the styling if label is same but level changed (e.g. Learning low -> high)
      els.masteryBadgeContainer.innerHTML = "";
      const badge = createBadgeElement(newInfo.label, newInfo.colorClass, newLevel);
      badge.classList.add("animate-glow");
      els.masteryBadgeContainer.appendChild(badge);
    }
  } else {
    // Level stayed the same, but still apply a subtle "pulse" to show it was processed
    const info = getMasteryInfo(newLevel);
    els.masteryBadgeContainer.innerHTML = "";
    const badge = createBadgeElement(info.label, info.colorClass, newLevel);
    badge.classList.add("animate-glow");
    els.masteryBadgeContainer.appendChild(badge);
  }

  const explanation = state.explanations && state.explanations[qId];
  if (explanation) {
    els.explanationText.innerHTML = `<strong>💡</strong> ${explanation}`;
    els.explanationText.style.display = "block";
  }

  const nextBtnContainer = document.getElementById("next-btn-container");
  if (nextBtnContainer) nextBtnContainer.style.display = "block";
  els.nextBtn.style.display = "block";
  updateStatsUI();
};

export const finishTest = () => {
  clearInterval(state.test.timerInterval);
  state.mode = null;
  window.dispatchEvent(new CustomEvent("hideAllViews"));
  els.results.style.display = "block";

  const score = state.test.score;
  const total = state.test.totalQuestions;
  const percent = Math.round((score / total) * 100);
  const passed = percent >= TEST_PASS_THRESHOLD * 100;

  els.resultTitle.textContent = passed ? "🎉 Test Passed!" : "❌ Test Failed";
  els.resultMessage.textContent = `You got ${score} out of ${total} questions correct (${percent}%).`;

  state.userProgress.testHistory.push({
    date: new Date().toISOString(),
    score,
    total,
    passed,
  });
  saveStats();
};

export const updateTimerUI = () => {
  const mins = Math.floor(state.test.timeLeft / 60);
  const secs = state.test.timeLeft % 60;
  els.timer.textContent = `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const updateStatsUI = () => {
  if (state.mode === "practice" || state.mode === "flashcard") {
    // If next button is visible, we have completed the question (sessionCount incremented),
    // but haven't moved to the next one yet. So current # is sessionCount.
    // If next button is hidden, we are working on the next question (sessionCount is 'completed'),
    // so current # is sessionCount + 1.
    const isAnswered = els.nextBtn.style.display === "block";
    const current = isAnswered ? state.practice.sessionCount : state.practice.sessionCount + 1;

    if (state.mode === "flashcard") {
      els.progress.textContent = `Card: ${current}`;
      els.score.textContent = "Study Mode";
      els.score.style.display = "inline";
    } else {
      els.progress.textContent = `Question: ${current}`;
      els.score.style.display = "none";
    }
  } else if (state.mode === "test") {
    els.progress.textContent = `Question ${state.test.currentIndex + 1} / ${state.test.totalQuestions}`;
    els.score.textContent = `Score: ${state.test.score}`;
    els.score.style.display = "inline";
  }
};

const createBadgeElement = (label, colorClass, level = 0) => {
  const badge = document.createElement("div");
  badge.className = `mastery-badge ${colorClass}`;
  badge.textContent = label;

  if (level >= MASTERY_THRESHOLD_MASTERED) {
    badge.classList.add("m-mastered");
  }

  return badge;
};
